import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronUp, Check, Plus, Search, X, CalendarClock, Package } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { badgeStyle } from "@/lib/badgeColor";
import { toast } from "sonner";

const emptyEntry = () => ({ discussion_notes: "", outcome_id: "", next_action: "", next_followup_date: "" });
const entryHasData = (e) => !!((e.discussion_notes || "").trim() || e.outcome_id || (e.next_action || "").trim() || e.next_followup_date);

export default function MeetingLogger() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const brokerParam = params.get("broker");

  const [brokerId, setBrokerId] = useState(brokerParam || "");
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 10));
  const [generalNotes, setGeneralNotes] = useState("");
  const [cards, setCards] = useState([]); // { asset_id, asset_name, asset_code, share_status_label, entry, open }
  const [expanded, setExpanded] = useState(null);
  const [brokerSearch, setBrokerSearch] = useState("");
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");

  // Outcome taxonomy
  const { data: outcomes = [] } = useQuery({
    queryKey: ["master", "broker_meeting_outcome"],
    queryFn: async () => (await api.get("/master-data/broker_meeting_outcome")).data,
  });

  // Broker list (for sidebar entry-point search)
  const { data: brokers = [] } = useQuery({
    queryKey: ["brokers-all-min"],
    queryFn: async () => (await api.get("/brokers")).data,
    enabled: !brokerParam && !isEdit,
  });

  // Edit mode — load the existing meeting and pre-fill everything.
  const { data: editMeeting } = useQuery({
    queryKey: ["meeting", editId],
    queryFn: async () => (await api.get(`/meetings/${editId}`)).data,
    enabled: isEdit,
  });
  useEffect(() => {
    if (editMeeting) {
      setBrokerId(editMeeting.broker_id);
      if (editMeeting.meeting_date) setMeetingDate(editMeeting.meeting_date.slice(0, 10));
      setGeneralNotes(editMeeting.general_notes || "");
      setCards((editMeeting.assets || []).map((e) => ({
        asset_id: e.asset_id, asset_name: e.asset_name, asset_code: e.asset_code, share_status_label: null,
        entry: {
          discussion_notes: e.discussion_notes || "", outcome_id: e.outcome_id || "",
          next_action: e.next_action || "", next_followup_date: (e.next_followup_date || "").slice(0, 10),
        },
      })));
    }
  }, [editMeeting]);

  // Prefill (broker + active shared assets) once a broker is chosen — CREATE mode only.
  const { data: prefill } = useQuery({
    queryKey: ["meeting-prefill", brokerId],
    queryFn: async () => (await api.get("/meetings/prefill", { params: { broker_id: brokerId } })).data,
    enabled: !!brokerId && !isEdit,
  });

  useEffect(() => {
    if (prefill?.assets && !isEdit) {
      setCards(prefill.assets.map((a) => ({ ...a, entry: emptyEntry() })));
    }
  }, [prefill, isEdit]);

  // Assets for "+ Add Another Asset"
  const { data: allAssets = [] } = useQuery({
    queryKey: ["assets-min-for-meeting"],
    queryFn: async () => (await api.get("/assets")).data,
    enabled: showAddAsset,
  });

  const brokerName = editMeeting?.broker_name || prefill?.broker_name || brokers.find((b) => b.id === brokerId)?.broker_name;

  const setEntry = (idx, patch) => {
    setCards((prev) => prev.map((c, i) => (i === idx ? { ...c, entry: { ...c.entry, ...patch } } : c)));
  };

  const filteredBrokers = useMemo(() => {
    const q = brokerSearch.trim().toLowerCase();
    if (!q) return brokers.slice(0, 30);
    return brokers.filter((b) => (b.broker_name || "").toLowerCase().includes(q)).slice(0, 30);
  }, [brokers, brokerSearch]);

  const filteredAssets = useMemo(() => {
    const existing = new Set(cards.map((c) => c.asset_id));
    const q = assetSearch.trim().toLowerCase();
    return allAssets
      .filter((a) => !existing.has(a.id))
      .filter((a) => !q || (a.name || "").toLowerCase().includes(q) || (a.asset_code || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [allAssets, cards, assetSearch]);

  const addAsset = (a) => {
    setCards((prev) => [...prev, { asset_id: a.id, asset_name: a.name, asset_code: a.asset_code, share_status_label: null, entry: emptyEntry() }]);
    setShowAddAsset(false);
    setAssetSearch("");
    setExpanded(cards.length);
  };

  const save = useMutation({
    mutationFn: async () => {
      const assets = cards
        .filter((c) => entryHasData(c.entry))
        .map((c) => ({
          asset_id: c.asset_id,
          discussion_notes: c.entry.discussion_notes || null,
          outcome_id: c.entry.outcome_id || null,
          next_action: c.entry.next_action || null,
          next_followup_date: c.entry.next_followup_date || null,
        }));
      const body = {
        broker_id: brokerId,
        meeting_date: meetingDate,
        general_notes: generalNotes || null,
        assets,
      };
      return isEdit
        ? (await api.patch(`/meetings/${editId}`, body)).data
        : (await api.post("/meetings", body)).data;
    },
    onSuccess: (data) => {
      if (isEdit) {
        toast.success("Meeting updated");
      } else {
        const t = data.created_tasks ? ` · ${data.created_tasks} follow-up task(s) created` : "";
        toast.success(`Meeting saved — ${data.created_entries} asset note(s)${t}`);
      }
      navigate(`/brokers/${brokerId}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save meeting"),
  });

  const notedCount = cards.filter((c) => entryHasData(c.entry)).length;

  // ----- Broker not yet chosen (sidebar entry point) -----
  if (!brokerId) {
    return (
      <div className="h-[calc(100dvh-3.5rem)] lg:h-[100dvh] flex flex-col overflow-hidden" data-testid="meeting-logger">
        <div className="shrink-0 p-4 border-b border-slate-200 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="meeting-back"><ArrowLeft className="h-4 w-4" /></Button>
          <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-slate-700" /><span className="font-display text-lg font-semibold">Log Meeting</span></div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 max-w-lg w-full mx-auto">
          <label className="label-eyebrow !text-slate-600">Select Broker</label>
          <div className="relative mt-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input autoFocus value={brokerSearch} onChange={(e) => setBrokerSearch(e.target.value)} placeholder="Search brokers…" className="pl-9" data-testid="meeting-broker-search" />
          </div>
          <div className="mt-3 space-y-1.5">
            {filteredBrokers.map((b) => (
              <button key={b.id} onClick={() => setBrokerId(b.id)} className="w-full text-left px-3 py-2.5 rounded-md border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors" data-testid={`meeting-broker-opt-${b.id}`}>
                <div className="text-sm font-medium text-slate-900">{b.broker_name}</div>
                {b.broker_type_label && <div className="text-xs text-slate-500">{b.broker_type_label}</div>}
              </button>
            ))}
            {filteredBrokers.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No brokers found.</div>}
          </div>
        </div>
      </div>
    );
  }

  // ----- Meeting Logger (broker chosen) -----
  return (
    <div className="h-[calc(100dvh-3.5rem)] lg:h-[100dvh] flex flex-col bg-slate-50 overflow-hidden" data-testid="meeting-logger">
      {/* Header */}
      <div className="shrink-0 p-4 border-b border-slate-200 bg-white flex items-center gap-2 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="meeting-back"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-2 min-w-0">
          <CalendarClock className="h-5 w-5 text-slate-700 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-base font-semibold text-slate-900 truncate" data-testid="meeting-broker-name">{brokerName || "Loading…"}</div>
            <div className="text-xs text-slate-500">{isEdit ? "Edit Meeting" : "Log Meeting"}</div>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 pb-6 max-w-2xl w-full mx-auto space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow !text-slate-600">Meeting Date</label>
            <Input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} className="mt-1" data-testid="meeting-date" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow !text-slate-600">General Notes</label>
          <Textarea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} rows={2} placeholder="Overall meeting notes, e.g. Met at broker's office, discussed Mumbai portfolio" className="mt-1" data-testid="meeting-general-notes" />
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="label-eyebrow !text-slate-700">Asset Discussion</span>
          <span className="text-xs text-slate-500">{notedCount} noted</span>
        </div>

        <div className="space-y-2.5">
          {cards.map((c, idx) => {
            const noted = entryHasData(c.entry);
            const open = expanded === idx;
            const outcome = outcomes.find((o) => o.id === c.entry.outcome_id);
            return (
              <div key={c.asset_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`meeting-asset-card-${c.asset_id}`}>
                <button
                  onClick={() => setExpanded(open ? null : idx)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                  data-testid={`meeting-asset-header-${c.asset_id}`}
                >
                  <Package className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.asset_name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {c.asset_code && <span className="text-[11px] text-slate-400 font-mono-num">{c.asset_code}</span>}
                      {c.share_status_label && <span className="text-[11px] text-slate-500">· {c.share_status_label}</span>}
                    </div>
                  </div>
                  {noted && <Check className="h-4 w-4 text-emerald-600 shrink-0" data-testid={`meeting-asset-noted-${c.asset_id}`} />}
                  {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>

                {open && (
                  <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                    <div>
                      <label className="label-eyebrow !text-slate-600">Discussion Notes</label>
                      <Textarea value={c.entry.discussion_notes} onChange={(e) => setEntry(idx, { discussion_notes: e.target.value })} rows={2} placeholder="What was discussed about this asset?" className="mt-1" data-testid={`meeting-notes-${c.asset_id}`} />
                    </div>
                    <div>
                      <label className="label-eyebrow !text-slate-600">Outcome</label>
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`meeting-outcome-${c.asset_id}`}>
                        {outcomes.map((o) => (
                          <button
                            key={o.id}
                            onClick={() => setEntry(idx, { outcome_id: c.entry.outcome_id === o.id ? "" : o.id })}
                            style={c.entry.outcome_id === o.id ? badgeStyle(o.badge_color) : undefined}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${c.entry.outcome_id === o.id ? "" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                            data-testid={`meeting-outcome-opt-${c.asset_id}-${o.id}`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="label-eyebrow !text-slate-600">Next Action</label>
                      <Input value={c.entry.next_action} onChange={(e) => setEntry(idx, { next_action: e.target.value })} placeholder="What needs to happen next?" className="mt-1" data-testid={`meeting-next-action-${c.asset_id}`} />
                    </div>
                    <div>
                      <label className="label-eyebrow !text-slate-600">Next Follow-up Date</label>
                      <Input type="date" value={c.entry.next_followup_date} onChange={(e) => setEntry(idx, { next_followup_date: e.target.value })} className="mt-1" data-testid={`meeting-followup-${c.asset_id}`} />
                      <p className="text-[11px] text-slate-400 mt-1">Setting a date auto-creates a follow-up task.</p>
                    </div>
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setExpanded(null)}>Collapse <ChevronUp className="h-3.5 w-3.5 ml-1" /></Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {cards.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-lg" data-testid="meeting-no-assets">
              No active shared assets. Use "Add Another Asset" to discuss any asset.
            </div>
          )}
        </div>

        {showAddAsset ? (
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="meeting-add-asset-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="label-eyebrow !text-slate-700">Add Asset</span>
              <Button variant="ghost" size="sm" onClick={() => setShowAddAsset(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input autoFocus value={assetSearch} onChange={(e) => setAssetSearch(e.target.value)} placeholder="Search assets…" className="pl-9" data-testid="meeting-asset-search" />
            </div>
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {filteredAssets.map((a) => (
                <button key={a.id} onClick={() => addAsset(a)} className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-slate-100" data-testid={`meeting-add-asset-opt-${a.id}`}>
                  <div className="text-sm font-medium text-slate-900">{a.name}</div>
                  {a.asset_code && <div className="text-[11px] text-slate-400 font-mono-num">{a.asset_code}</div>}
                </button>
              ))}
              {filteredAssets.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No assets found.</div>}
            </div>
          </div>
        ) : (
          <Button variant="outline" className="w-full" onClick={() => setShowAddAsset(true)} data-testid="meeting-add-asset-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Another Asset
          </Button>
        )}
      </div>

      {/* Footer pinned inside the dynamic viewport — always reachable above mobile keyboard */}
      <div className="shrink-0 bg-white border-t border-slate-200 p-3 flex items-center gap-2 max-w-2xl w-full mx-auto">
        <Button variant="outline" className="flex-1" onClick={() => navigate(-1)} data-testid="meeting-cancel">Cancel</Button>
        <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={() => save.mutate()} disabled={save.isPending || (!isEdit && notedCount === 0)} data-testid="meeting-save">
          {save.isPending ? "Saving…" : (isEdit ? "Update Meeting" : "Save Meeting")}
        </Button>
      </div>
    </div>
  );
}
